import { useMemo } from 'react';
import { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { createIconElement, formatCurrency, formatNumber } from '@/lib';
import { DropdownProvider } from '@/components/core-components/StanDropdown/DropdownProvider';
import { StanDropdownMenu } from '@/components/core-components/StanDropdown/Menu/StanDropdownMenu';
import CopyIconSvg from '@/assets/icons/ic-copy.svg';
import IcArrowRight from '@/assets/icons/ic-arrow-right.svg';
import IcMoreVertical from '@/assets/icons/ic-more-vertical.svg';
import type { AffiliateLink, UseAffiliateLinksColumnsProps } from '../types/affiliateLinks.types';
import { removeHttps } from '@/lib/helpers';
import { useUser } from '@/queries/useUser';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { StanButton } from '@/components';

const copyIcon = createIconElement(CopyIconSvg, { className: 'size-4' });
const arrowIcon = createIconElement(IcArrowRight, { className: 'size-4' });
const moreVerticalIcon = createIconElement(IcMoreVertical, { className: 'size-5' });

// Helper render functions since we have a different layout on mobile, card view instead of table
const renderProductInfo = (link: AffiliateLink, size: 'sm' | 'md' = 'sm') => {
    const imageSize = size === 'md' ? 'size-12' : 'size-10';
    return (
        <div className='flex items-center gap-3'>
            <img
                src={link.product_image}
                alt={link.product}
                className={`${imageSize} shrink-0 rounded-md object-cover`}
            />
            <div className='flex flex-col'>
                <span className='text-dark text-label-sm-bold'>{link.product}</span>
                <span className='text-gray text-label-xs-regular'>{formatCurrency(link.price, link.currency)}</span>
            </div>
        </div>
    );
};

const renderCreatorInfo = (link: AffiliateLink, withLabel = false) => {
    if (withLabel) {
        return (
            <div className='flex items-center justify-between'>
                <span className='text-caption-1-regular text-text-secondary'>Creator</span>
                <div className='flex items-center gap-2'>
                    <img
                        src={link.product_owner_pic}
                        alt={link.product_owner}
                        className='size-6 shrink-0 rounded-full object-cover'
                    />
                    <span className='text-caption-1-bold text-text-primary'>@{link.product_owner_username}</span>
                </div>
            </div>
        );
    }

    return (
        <div className='flex items-center gap-2'>
            <img
                src={link.product_owner_pic}
                alt={link.product_owner}
                className='size-8 shrink-0 rounded-full object-cover'
            />
            <div className='flex flex-col'>
                <span className='text-dark text-label-sm-bold'>{link.product_owner}</span>
                <span className='text-gray text-label-xs-regular'>@{link.product_owner_username}</span>
            </div>
        </div>
    );
};

const renderURLCell = (
    link: AffiliateLink,
    onCopy: (url: string) => void,
    onCompleteSetup: (link: AffiliateLink) => void,
    showLabel: boolean,
    isAffiliateOnly: boolean,
    setupLoading: boolean
) => {
    const isPendingSetup = link.status === 'pending-setup';
    const url = link.affiliate_link;

    // URL display or "Complete Setup" button
    const urlContent = isPendingSetup ? (
        <div className='flex items-center justify-center rounded-lg py-2'>
            <StanButton
                name={`complete-setup-${link.id}`}
                size='xs'
                variant='outline'
                onClick={() => onCompleteSetup(link)}
                loading={setupLoading}
                disabled={setupLoading}
            >
                Complete Setup {arrowIcon}
            </StanButton>
        </div>
    ) : (
        <div
            className='bg-brand-subtle text-brand-main hover:bg-brand-soft flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 transition-colors'
            onClick={() => onCopy(url)}
        >
            <span className='text-label-sm-bold flex-1 truncate'>{removeHttps(url)}</span>
            {copyIcon}
        </div>
    );

    if (showLabel) {
        return (
            <div className='flex flex-col gap-2'>
                <span className='text-caption-1-regular text-text-secondary'>
                    {isAffiliateOnly ? 'Resell URL' : 'Product URL'}
                </span>
                {urlContent}
            </div>
        );
    }

    return urlContent;
};

const renderActionsMenu = (link: AffiliateLink, onTransfer: (link: AffiliateLink) => void, dropdownId: string) => {
    // Hide transfer option if not allowed or if setup is pending (transfer won't work until setup is complete)
    if (!link.allow_transfer || link.status === 'pending-setup') return null;

    return (
        <DropdownProvider dropdownId={dropdownId} dropdownName='affiliate-link-actions'>
            <StanDropdownMenu
                id={`${dropdownId}-menu`}
                name={`${dropdownId}-menu`}
                options={[{ label: 'Transfer Affiliate Link', value: 'transfer' }]}
                onSelect={() => onTransfer(link)}
                placement='bottom-end'
            >
                <button className='hover:bg-neutral-subtle flex size-8 cursor-pointer items-center justify-center rounded-md'>
                    {moreVerticalIcon}
                </button>
            </StanDropdownMenu>
        </DropdownProvider>
    );
};

export function useAffiliateLinksColumns({
    onTransfer,
    onCopyUrl,
    onCompleteSetup,
    setupLoadingLinkId,
}: UseAffiliateLinksColumnsProps): StanColumnDef<AffiliateLink>[] {
    const { user } = useUser();
    const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;
    const isMobile = useMediaQuery('(max-width: 768px)');

    return useMemo(
        () => [
            {
                header: 'Product',
                accessorKey: 'product',
                width: isMobile ? '100%' : '20%',
                cell: info => {
                    const link = info.row.original;

                    // Mobile: Full card layout with all data stacked
                    if (isMobile) {
                        return (
                            <div className='flex w-full flex-col gap-3 py-3'>
                                {/* Product info with action menu */}
                                <div className='flex items-center justify-between gap-3'>
                                    {renderProductInfo(link, 'md')}
                                    {renderActionsMenu(link, onTransfer, `affiliate-link-mobile-${link.id}`)}
                                </div>

                                {/* Creator */}
                                {renderCreatorInfo(link, true)}

                                {/* Resell % */}
                                <div className='flex items-center justify-between'>
                                    <span className='text-caption-1-regular text-text-secondary'>Resell %</span>
                                    <span className='text-caption-1-bold text-text-primary'>{link.commission}%</span>
                                </div>

                                {/* Link Clicks */}
                                <div className='flex items-center justify-between'>
                                    <span className='text-caption-1-regular text-text-secondary'>Link Clicks</span>
                                    <span className='text-caption-1-bold text-text-primary'>
                                        {formatNumber(link.views, { decimals: 0 })}
                                    </span>
                                </div>

                                {/* # of Sales */}
                                <div className='flex items-center justify-between'>
                                    <span className='text-caption-1-regular text-text-secondary'># of Sales</span>
                                    <span className='text-caption-1-bold text-text-primary'>
                                        {formatNumber(link.number_of_sales, { decimals: 0 })}
                                    </span>
                                </div>

                                {/* Your Income */}
                                <div className='flex items-center justify-between'>
                                    <span className='text-caption-1-regular text-text-secondary'>Your Income</span>
                                    <span className='text-dark text-label-sm-bold'>
                                        {formatCurrency(link.total_sales_amount)}
                                    </span>
                                </div>

                                {/* URL */}
                                {renderURLCell(
                                    link,
                                    onCopyUrl,
                                    onCompleteSetup,
                                    true,
                                    isAffiliateOnlySubscription,
                                    setupLoadingLinkId === link.id
                                )}
                            </div>
                        );
                    }

                    // Desktop: Regular layout
                    return <div className='pt-3 pb-3'>{renderProductInfo(link, 'sm')}</div>;
                },
            },
            {
                header: 'Creator',
                accessorKey: 'product_owner',
                width: '20%',
                hideOnMobile: true,
                cell: info => renderCreatorInfo(info.row.original, false),
            },
            {
                header: 'Resell %',
                accessorKey: 'commission',
                width: '10%',
                hideOnMobile: true,
                cell: info => <span className='text-dark text-label-sm-regular'>{info.getValue()}%</span>,
            },
            {
                header: 'Link Clicks',
                accessorKey: 'views',
                width: '10%',
                hideOnMobile: true,
                cell: info => (
                    <span className='text-dark text-label-sm-regular'>
                        {formatNumber(info.getValue() as number, { decimals: 0 })}
                    </span>
                ),
            },
            {
                header: '# of Sales',
                accessorKey: 'number_of_sales',
                width: '10%',
                hideOnMobile: true,
                cell: info => (
                    <span className='text-dark text-label-sm-regular'>
                        {formatNumber(info.getValue() as number, { decimals: 0 })}
                    </span>
                ),
            },
            {
                header: 'Your Income',
                accessorKey: 'total_sales_amount',
                width: '10%',
                hideOnMobile: true,
                cell: info => (
                    <span className='text-dark text-label-sm-bold'>{formatCurrency(info.getValue() as number)}</span>
                ),
            },
            {
                header: isAffiliateOnlySubscription ? 'Resell URL' : 'Product URL',
                accessorKey: 'affiliate_link',
                width: '20%',
                hideOnMobile: true,
                cell: info =>
                    renderURLCell(
                        info.row.original,
                        onCopyUrl,
                        onCompleteSetup,
                        false,
                        isAffiliateOnlySubscription,
                        setupLoadingLinkId === info.row.original.id
                    ),
            },
            {
                header: '',
                accessorKey: 'actions',
                width: '5%',
                hideOnMobile: true,
                cell: info => {
                    const link = info.row.original;
                    return (
                        <div className='flex justify-end'>
                            {renderActionsMenu(link, onTransfer, `affiliate-link-${link.id}`)}
                        </div>
                    );
                },
            },
        ],
        [onTransfer, onCopyUrl, onCompleteSetup, setupLoadingLinkId, isAffiliateOnlySubscription, isMobile]
    );
}
